import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { 
  Table, 
  Info, 
  Lightbulb, 
  AlertTriangle, 
  ShieldAlert, 
  Bookmark, 
  GitBranch, 
  CheckCircle2, 
  ChevronRight, 
  ArrowDown, 
  Boxes, 
  Layers, 
  Sparkles,
  Copy,
  Check
} from 'lucide-react';

interface ArticleContentRendererProps {
  content: string;
  className?: string;
}

// Helper to parse structure blocks
function renderStructureTree(rawContent: string) {
  const lines = rawContent.split('\n').filter(line => line.trim().length > 0);
  let title = '';
  const nodes: { level: number; label: string; sub?: string }[] = [];

  for (const line of lines) {
    if (line.toLowerCase().startsWith('title:')) {
      title = line.replace(/title:/i, '').trim();
      continue;
    }

    // Determine indentation level
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Detect indentation
    const indentMatch = line.match(/^(\s*)/);
    const indentSpaces = indentMatch ? indentMatch[1].length : 0;
    
    // Check if it has arrows or bullets
    let cleanText = trimmed
      .replace(/^([•●▪▫◦\-\+\*]|->|=>|├──|└──|│)\s*/, '')
      .replace(/^\[(.*?)\]$/, '$1')
      .trim();

    let subText = '';
    if (cleanText.includes('|')) {
      const parts = cleanText.split('|');
      cleanText = parts[0].trim();
      subText = parts.slice(1).join('|').trim();
    } else if (cleanText.includes(' - ')) {
      const parts = cleanText.split(' - ');
      cleanText = parts[0].trim();
      subText = parts.slice(1).join(' - ').trim();
    }

    const level = Math.min(Math.floor(indentSpaces / 2), 4);
    nodes.push({ level, label: cleanText, sub: subText });
  }

  return (
    <div className="my-8 p-6 rounded-3xl bg-gradient-to-b from-card to-muted/30 border-2 border-gold/30 shadow-xl overflow-hidden">
      {title && (
        <div className="flex items-center gap-2.5 pb-4 mb-6 border-b border-border">
          <div className="p-2 rounded-xl bg-gold/15 text-gold">
            <GitBranch className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-black text-foreground text-base md:text-lg tracking-tight">{title}</h4>
            <p className="text-[11px] text-foreground/50 font-medium">Bagan Struktur Organisasi & Hirarki</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {nodes.map((node, idx) => {
          const isRoot = node.level === 0;
          return (
            <div 
              key={idx} 
              className="flex items-start gap-3 transition-all"
              style={{ paddingLeft: `${node.level * 24}px` }}
            >
              <div className="flex flex-col items-center shrink-0 pt-1">
                {isRoot ? (
                  <div className="w-4 h-4 rounded-full bg-gold border-2 border-background shadow-sm" />
                ) : (
                  <div className="w-2.5 h-2.5 rounded-full bg-primary/60 border border-background" />
                )}
              </div>
              <div className={`flex-1 p-3.5 rounded-2xl border transition-all ${
                isRoot 
                  ? 'bg-primary text-primary-foreground border-primary shadow-md font-bold' 
                  : node.level === 1
                  ? 'bg-card text-foreground border-gold/40 shadow-sm font-semibold'
                  : 'bg-muted/40 text-foreground/90 border-border/70 text-sm'
              }`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className={`${isRoot ? 'text-primary-foreground' : 'text-foreground'} font-bold text-sm md:text-base`}>
                    {node.label}
                  </span>
                  {node.sub && (
                    <span className={`text-xs px-2.5 py-0.5 rounded-lg w-fit ${
                      isRoot 
                        ? 'bg-primary-foreground/20 text-primary-foreground' 
                        : 'bg-gold/10 text-gold border border-gold/20'
                    }`}>
                      {node.sub}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper to parse workflow / step-by-step blocks
function renderWorkflow(rawContent: string) {
  const lines = rawContent.split('\n').filter(line => line.trim().length > 0);
  let title = '';
  const steps: { number: number; title: string; desc: string }[] = [];

  let currentNum = 1;
  for (const line of lines) {
    if (line.toLowerCase().startsWith('title:')) {
      title = line.replace(/title:/i, '').trim();
      continue;
    }

    let clean = line.trim();
    // Match numbering like "1. ", "Step 1:", "Tahap 1:"
    const numMatch = clean.match(/^(\d+|Tahap \d+|Langkah \d+)[\.:\-\s]+(.*)/i);
    let stepTitle = clean;
    if (numMatch) {
      stepTitle = numMatch[2].trim();
    }

    let desc = '';
    if (stepTitle.includes('|')) {
      const parts = stepTitle.split('|');
      stepTitle = parts[0].trim();
      desc = parts.slice(1).join('|').trim();
    } else if (stepTitle.includes(' - ')) {
      const parts = stepTitle.split(' - ');
      stepTitle = parts[0].trim();
      desc = parts.slice(1).join(' - ').trim();
    }

    steps.push({
      number: currentNum++,
      title: stepTitle,
      desc
    });
  }

  return (
    <div className="my-8 p-6 md:p-8 rounded-3xl bg-card border-2 border-primary/20 shadow-xl">
      {title && (
        <div className="flex items-center gap-3 pb-5 mb-6 border-b border-border">
          <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-black text-foreground text-lg tracking-tight">{title}</h4>
            <p className="text-xs text-foreground/50 font-medium">Alur Kerja & Tahapan Prosedural</p>
          </div>
        </div>
      )}

      <div className="relative pl-6 md:pl-8 space-y-6 before:absolute before:left-3 md:before:left-4 before:top-3 before:bottom-3 before:w-0.5 before:bg-gradient-to-b before:from-gold before:via-primary before:to-muted">
        {steps.map((step) => (
          <div key={step.number} className="relative group">
            <div className="absolute -left-6 md:-left-8 top-1.5 w-6 h-6 md:w-8 md:h-8 rounded-full bg-gold text-navy font-black text-xs md:text-sm flex items-center justify-center shadow-md ring-4 ring-card">
              {step.number}
            </div>
            <div className="bg-muted/40 group-hover:bg-muted/70 p-4 md:p-5 rounded-2xl border border-border transition-all">
              <h5 className="font-bold text-foreground text-sm md:text-base mb-1">{step.title}</h5>
              {step.desc && (
                <p className="text-xs md:text-sm text-foreground/70 leading-relaxed font-normal">{step.desc}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper to parse grid card boxes
function renderCardGrid(rawContent: string) {
  const items = rawContent.split(/---|\n\n/).filter(item => item.trim().length > 0);
  
  return (
    <div className="my-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((item, idx) => {
        const lines = item.trim().split('\n');
        const header = lines[0].replace(/^#+\s*|\*\*|\*/g, '').trim();
        const body = lines.slice(1).join('\n').trim();

        return (
          <div key={idx} className="p-5 rounded-2xl bg-card border border-gold/30 hover:border-gold shadow-md hover:shadow-lg transition-all flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2.5 h-2.5 rounded-full bg-gold" />
              <h5 className="font-black text-foreground text-sm md:text-base">{header}</h5>
            </div>
            {body && (
              <p className="text-xs md:text-sm text-foreground/75 leading-relaxed mt-1 flex-1">{body}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ArticleContentRenderer({ content, className = '' }: ArticleContentRendererProps) {
  return (
    <div className={`article-content prose prose-lg dark:prose-invert max-w-none prose-headings:font-black prose-headings:tracking-tighter prose-headings:text-foreground prose-p:leading-[1.8] prose-p:text-foreground/80 prose-justified prose-strong:text-foreground prose-a:text-gold hover:prose-a:text-gold-light prose-img:rounded-3xl prose-img:shadow-lg ${className}`}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          // Paragraph
          p: ({ children }) => <p className="mb-6 leading-[1.8] text-foreground/80">{children}</p>,
          
          // Headings
          h1: ({ children }) => <h1 className="mt-14 mb-6 text-3xl md:text-4xl font-black text-foreground border-b border-border/60 pb-3">{children}</h1>,
          h2: ({ children }) => (
            <h2 className="mt-12 mb-5 text-2xl md:text-3xl font-black text-foreground flex items-center gap-2.5">
              <span className="w-2 h-7 rounded-full bg-gold inline-block shrink-0" />
              <span>{children}</span>
            </h2>
          ),
          h3: ({ children }) => <h3 className="mt-8 mb-4 text-xl md:text-2xl font-bold text-foreground">{children}</h3>,
          h4: ({ children }) => <h4 className="mt-6 mb-3 text-lg font-bold text-foreground">{children}</h4>,

          // Lists
          ul: ({ children }) => <ul className="list-disc list-outside ml-6 mb-6 space-y-2 text-foreground/85">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-outside ml-6 mb-6 space-y-2 text-foreground/85">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed pl-1">{children}</li>,

          // Tables - Beautiful & Mobile-Responsive
          table: ({ children }) => (
            <div className="my-8 rounded-2xl border border-border shadow-md bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse min-w-[500px]">
                  {children}
                </table>
              </div>
              <div className="bg-muted/40 px-4 py-2 border-t border-border flex items-center justify-between text-[11px] text-foreground/50">
                <span className="flex items-center gap-1.5 font-bold text-gold">
                  <Table className="h-3.5 w-3.5" /> Tabel Rincian & Data Teknis
                </span>
                <span className="text-[10px] hidden sm:inline">Geser horizontal jika tampilan di ponsel terpotong</span>
              </div>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-primary text-primary-foreground border-b-2 border-gold/40">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3.5 text-left font-black text-xs uppercase tracking-wider text-primary-foreground border-r border-primary-foreground/10 last:border-r-0">
              {children}
            </th>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-border/60">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-muted/50 transition-colors even:bg-muted/15">
              {children}
            </tr>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3.5 text-sm text-foreground/85 border-r border-border/40 last:border-r-0 align-top">
              {children}
            </td>
          ),

          // Blockquotes with Intelligent Callout Support
          blockquote: ({ children }) => {
            // Check text of children to detect callout syntax
            const rawText = React.Children.toArray(children)
              .map(child => (typeof child === 'string' ? child : ''))
              .join('');

            // Callout types
            const isInfo = /\[!(NOTE|INFO)\]/i.test(rawText) || /\[INFO\]/i.test(rawText);
            const isTip = /\[!(TIP|TIPS)\]/i.test(rawText) || /\[TIPS\]/i.test(rawText);
            const isWarning = /\[!(WARNING|PERINGATAN|HATI-HATI)\]/i.test(rawText) || /\[PERINGATAN\]/i.test(rawText);
            const isImportant = /\[!(IMPORTANT|PENTING)\]/i.test(rawText) || /\[PENTING\]/i.test(rawText);
            const isStructure = /\[!(STRUKTUR|BAGAN|ORGANISASI)\]/i.test(rawText) || /\[STRUKTUR\]/i.test(rawText);

            if (isInfo) {
              return (
                <div className="my-6 p-5 md:p-6 rounded-2xl bg-blue-500/10 border-l-4 border-blue-500 text-foreground shadow-sm">
                  <div className="flex items-center gap-2 mb-2 font-black text-blue-600 dark:text-blue-400 text-sm uppercase tracking-wider">
                    <Info className="h-4 w-4" /> Informasi Penting
                  </div>
                  <div className="text-sm md:text-base leading-relaxed text-foreground/85 [&>p]:mb-2 [&>p:last-child]:mb-0">
                    {children}
                  </div>
                </div>
              );
            }

            if (isTip) {
              return (
                <div className="my-6 p-5 md:p-6 rounded-2xl bg-emerald-500/10 border-l-4 border-emerald-500 text-foreground shadow-sm">
                  <div className="flex items-center gap-2 mb-2 font-black text-emerald-600 dark:text-emerald-400 text-sm uppercase tracking-wider">
                    <Lightbulb className="h-4 w-4" /> Tips & Rekomendasi
                  </div>
                  <div className="text-sm md:text-base leading-relaxed text-foreground/85 [&>p]:mb-2 [&>p:last-child]:mb-0">
                    {children}
                  </div>
                </div>
              );
            }

            if (isWarning) {
              return (
                <div className="my-6 p-5 md:p-6 rounded-2xl bg-amber-500/10 border-l-4 border-amber-500 text-foreground shadow-sm">
                  <div className="flex items-center gap-2 mb-2 font-black text-amber-600 dark:text-amber-400 text-sm uppercase tracking-wider">
                    <AlertTriangle className="h-4 w-4" /> Perhatian / Regulasi Kritis
                  </div>
                  <div className="text-sm md:text-base leading-relaxed text-foreground/85 [&>p]:mb-2 [&>p:last-child]:mb-0">
                    {children}
                  </div>
                </div>
              );
            }

            if (isImportant) {
              return (
                <div className="my-6 p-5 md:p-6 rounded-2xl bg-gold/15 border-l-4 border-gold text-foreground shadow-md ring-1 ring-gold/20">
                  <div className="flex items-center gap-2 mb-2 font-black text-gold text-sm uppercase tracking-wider">
                    <Bookmark className="h-4 w-4" /> Sorotan Utama (Key Highlight)
                  </div>
                  <div className="text-sm md:text-base leading-relaxed text-foreground/90 font-medium [&>p]:mb-2 [&>p:last-child]:mb-0">
                    {children}
                  </div>
                </div>
              );
            }

            if (isStructure) {
              return (
                <div className="my-6 p-6 rounded-3xl bg-card border-2 border-gold/40 shadow-xl">
                  <div className="flex items-center gap-2 mb-3 font-black text-gold text-sm uppercase tracking-wider">
                    <Boxes className="h-4 w-4" /> Bagan Struktur / Hirarki
                  </div>
                  <div className="text-sm md:text-base leading-relaxed text-foreground [&>p]:mb-2 [&>p:last-child]:mb-0">
                    {children}
                  </div>
                </div>
              );
            }

            // Standard elegant quote
            return (
              <blockquote className="border-l-4 border-gold bg-gold/5 p-6 rounded-r-2xl my-8 italic text-foreground/90">
                {children}
              </blockquote>
            );
          },

          // Code blocks with special diagram/structure languages
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const lang = match ? match[1].toLowerCase() : '';
            const codeString = String(children).replace(/\n$/, '');

            if (lang === 'structure' || lang === 'bagan' || lang === 'organisasi') {
              return renderStructureTree(codeString);
            }

            if (lang === 'alur' || lang === 'workflow' || lang === 'langkah') {
              return renderWorkflow(codeString);
            }

            if (lang === 'kotak' || lang === 'card' || lang === 'grid') {
              return renderCardGrid(codeString);
            }

            // Inline code
            if (!match) {
              return (
                <code className="bg-muted px-2 py-0.5 rounded-lg text-xs md:text-sm font-mono text-gold font-bold border border-border" {...props}>
                  {children}
                </code>
              );
            }

            // Regular code block
            return (
              <div className="my-6 rounded-2xl overflow-hidden border border-border shadow-md bg-card">
                <div className="bg-muted px-4 py-2 border-b border-border flex items-center justify-between text-xs font-mono text-foreground/60">
                  <span>{lang.toUpperCase()}</span>
                </div>
                <pre className="p-4 overflow-x-auto text-xs md:text-sm font-mono bg-card text-foreground">
                  <code>{children}</code>
                </pre>
              </div>
            );
          },

          // Horizontal rule
          hr: () => <hr className="my-10 border-t-2 border-border/60" />,

          // Links
          a: ({ href, children }) => (
            <a 
              href={href} 
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="text-gold font-bold underline underline-offset-4 hover:text-gold-light transition-colors"
            >
              {children}
            </a>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
